import { useEffect, useRef, useState } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  githubAuthApiRef,
  discoveryApiRef,
  useApi,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import { Typography, Box, CircularProgress } from '@material-ui/core';

// Always clear the legacy guest token flag — it bypasses backend auth
localStorage.removeItem('enableLegacyGuestToken');

/**
 * Custom guest sign-in for /tv. Calls the proxy-based guest auth provider's
 * refresh endpoint, which returns the identity as JSON (no cookies).
 * We construct an IdentityApi from the response and hand it to Backstage.
 */
function TvGuestSignIn(props: SignInPageProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const attempted = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('auth');
        const url = `${baseUrl}/guest/refresh`;
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${body}`);
        }
        const data = await res.json();

        const { backstageIdentity, profile } = data;

        props.onSignInSuccess({
          getBackstageIdentity: async () => backstageIdentity.identity,
          getProfileInfo: async () => profile,
          getCredentials: async () => ({ token: backstageIdentity.token }),
          signOut: async () => {},
        });
      } catch (err: any) {
        setError(err?.message || String(err));
      }
    })();
  }, [discoveryApi, props]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ height: '100vh', background: '#0d1117', color: '#e6edf3' }}
      >
        <Typography variant="h5" style={{ marginBottom: 16 }}>
          TV Dashboard — Access Denied
        </Typography>
        <Typography variant="body1" style={{ color: '#8b949e' }}>
          Guest access is restricted to the office network.
        </Typography>
        <Typography variant="caption" style={{ color: '#6e7681', marginTop: 12, maxWidth: 600, wordBreak: 'break-all', textAlign: 'center' }}>
          {error}
        </Typography>
        <Typography
          variant="body2"
          style={{ color: '#58a6ff', marginTop: 24, cursor: 'pointer' }}
          onClick={() => { window.location.href = '/'; }}
        >
          Sign in with GitHub instead
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{ height: '100vh', background: '#0d1117' }}
    >
      <CircularProgress style={{ color: '#58a6ff' }} />
    </Box>
  );
}

export const signInPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => props => {
          if (window.location.pathname.startsWith('/tv')) {
            return <TvGuestSignIn {...props} />;
          }

          return (
            <SignInPage
              {...props}
              providers={[
                {
                  id: 'github-auth-provider',
                  title: 'GitHub',
                  message: 'Sign in using GitHub',
                  apiRef: githubAuthApiRef,
                },
              ]}
            />
          );
        },
      },
    }),
  ],
});
